import { Navigate } from '@tanstack/react-router';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { Button } from '@/components/ui/button';
import { Campo, Input } from '@/components/ui/form';

export function LoginPage() {
  const { estado, iniciarSesion } = useSesion();
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (estado.tipo === 'autenticado') return <Navigate to="/" />;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await iniciarSesion(usuario.trim(), password);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      {/* Panel de marca: tinta con el sello grabado. */}
      <section className="relative hidden overflow-hidden bg-[#141414] text-[#EDEAE3] lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, #EDEAE3 0 1px, transparent 1px 14px)',
          }}
          aria-hidden
        />
        <p className="relative text-[12px] uppercase tracking-[0.35em] text-[#C9A063]">Barbería · Est. 2026</p>
        <div className="relative">
          <img
            src="/logo-sello.jpeg"
            alt=""
            className="mb-10 size-44 rounded-full opacity-95 invert mix-blend-screen"
          />
          <h1 className="font-display text-[56px] leading-[1.02] tracking-tight">
            El oficio,
            <br />
            <em className="text-[#C9A063]">en orden.</em>
          </h1>
          <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-[#A8A298]">
            Agenda, caja, inventario y equipo de Barbones en un solo lugar.
          </p>
        </div>
        <div className="relative h-px w-40 bg-gradient-to-r from-[#C9A063] to-transparent" aria-hidden />
      </section>

      <section className="flex items-center justify-center px-6 py-12">
        <form onSubmit={enviar} className="w-full max-w-sm animate-entrar" noValidate>
          <img src="/logo-completo.jpeg" alt="Barbones Barbería" className="mx-auto mb-8 w-48 mix-blend-multiply dark:invert dark:mix-blend-screen lg:hidden" />
          <h2 className="font-display text-[32px] leading-tight">Iniciar sesión</h2>
          <p className="mt-1 text-[14px] text-foreground-muted">Ingresa con tu usuario del sistema.</p>
          <div className="filete mt-4 w-24" aria-hidden />

          <div className="mt-8 flex flex-col gap-4">
            <Campo etiqueta="Usuario" requerido>
              {(p) => (
                <Input
                  {...p}
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoFocus
                  required
                  className="h-11"
                />
              )}
            </Campo>
            <Campo etiqueta="Contraseña" requerido>
              {(p) => (
                <div className="relative">
                  <Input
                    {...p}
                    type={verPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="h-11 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setVerPassword((v) => !v)}
                    className="absolute right-1 top-1 grid size-9 place-items-center rounded-md text-foreground-muted hover:bg-muted"
                    aria-label={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {verPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              )}
            </Campo>

            {error && (
              <p role="alert" className="rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-[13px] text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" tamano="lg" cargando={enviando} disabled={!usuario || !password} className="mt-2 w-full">
              <LogIn />
              Entrar
            </Button>
          </div>
          <p className="mt-8 text-center text-[12px] text-foreground-muted">
            ¿Olvidaste tu contraseña? Pide a recepción o al dueño que la restablezca.
          </p>
        </form>
      </section>
    </div>
  );
}
